import React, { useState, useEffect } from "react";
import axios from "axios";

const NotificationManagement = () => {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [targetRecipients, setTargetRecipients] = useState("all");
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");

  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const token = userData.token;

  useEffect(() => {
    if (targetRecipients === "specific") {
      fetchUsers();
    }
  }, [targetRecipients]);

  useEffect(() => {
    // Filter users based on search query
    if (targetRecipients === "specific") {
      const filtered = users.filter(
        (user) =>
          user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          user.email.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setFilteredUsers(filtered);
    }
  }, [searchQuery, users, targetRecipients]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/api/users/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
      setErrorMessage("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  const handleUserToggle = (userId) => {
    setSelectedUsers((prev) =>
      prev.includes(userId)
        ? prev.filter((id) => id !== userId)
        : [...prev, userId]
    );
  };

  const handleSelectAllVisibleUsers = () => {
    const visibleUserIds = filteredUsers.map((user) => user._id);
    setSelectedUsers((prev) => {
      const allSelected = visibleUserIds.every((id) => prev.includes(id));
      if (allSelected) {
        return prev.filter((id) => !visibleUserIds.includes(id));
      } else {
        const notSelected = visibleUserIds.filter((id) => !prev.includes(id));
        return [...prev, ...notSelected];
      }
    });
  };

  const handleSendNotification = async (e) => {
    e.preventDefault();

    if (!title.trim() || !message.trim()) {
      setErrorMessage("Title and message are required");
      return;
    }

    if (targetRecipients === "specific" && selectedUsers.length === 0) {
      setErrorMessage("Please select at least one user");
      return;
    }

    try {
      setSending(true);
      setErrorMessage("");
      setSuccessMessage("");

      const payload = {
        title: title.trim(),
        message: message.trim(),
        targetRecipients,
      };

      if (targetRecipients === "specific") {
        payload.recipientIds = selectedUsers;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/notifications/send-system`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setSuccessMessage(`✓ ${response.data.message}`);

      // Reset form
      setTitle("");
      setMessage("");
      setTargetRecipients("all");
      setSelectedUsers([]);
      setSearchQuery("");

      setTimeout(() => setSuccessMessage(""), 5000);
    } catch (error) {
      const errorMsg =
        error.response?.data?.error || "Failed to send notification";
      setErrorMessage(errorMsg);
      console.error("Error sending notification:", error);
    } finally {
      setSending(false);
    }
  };

  const colors = {
    mainBlue: "#061941",
    xanthous: "#F2AF29",
    isabelline: "#F4F3EE",
    carrieban: "#3C6E71",
    muted: "#6B7280",
    success: "#10B981",
    error: "#EF4444",
  };

  return (
    <div style={{ padding: "20px" }}>
      <h2
        style={{
          fontSize: "24px",
          fontWeight: "700",
          color: colors.mainBlue,
          marginBottom: "24px",
        }}
      >
        Send System Notification
      </h2>

      {successMessage && (
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: colors.success + "20",
            color: colors.success,
            borderRadius: "6px",
            marginBottom: "16px",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          {successMessage}
        </div>
      )}

      {errorMessage && (
        <div
          style={{
            padding: "12px 16px",
            backgroundColor: colors.error + "20",
            color: colors.error,
            borderRadius: "6px",
            marginBottom: "16px",
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          {errorMessage}
        </div>
      )}

      <form
        onSubmit={handleSendNotification}
        style={{
          maxWidth: "900px",
          backgroundColor: "white",
          padding: "24px",
          borderRadius: "8px",
          boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        }}
      >
        {/* Title Field */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: colors.mainBlue,
              marginBottom: "8px",
            }}
          >
            Notification Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="e.g., System Update"
            style={{
              width: "100%",
              padding: "10px 12px",
              border: `1px solid ${colors.mainBlue}30`,
              borderRadius: "6px",
              fontSize: "14px",
              boxSizing: "border-box",
              fontFamily: "inherit",
            }}
          />
        </div>

        {/* Message Field */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: colors.mainBlue,
              marginBottom: "8px",
            }}
          >
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            placeholder="Enter your message here..."
            rows={6}
            style={{
              width: "100%",
              padding: "10px 12px",
              border: `1px solid ${colors.mainBlue}30`,
              borderRadius: "6px",
              fontSize: "14px",
              boxSizing: "border-box",
              fontFamily: "inherit",
              resize: "vertical",
            }}
          />
        </div>

        {/* Target Recipients */}
        <div style={{ marginBottom: "20px" }}>
          <label
            style={{
              display: "block",
              fontSize: "14px",
              fontWeight: "600",
              color: colors.mainBlue,
              marginBottom: "12px",
            }}
          >
            Send To
          </label>
          <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
            {["all", "professors", "students", "specific"].map((option) => (
              <label
                key={option}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                <input
                  type="radio"
                  name="targetRecipients"
                  value={option}
                  checked={targetRecipients === option}
                  onChange={(e) => {
                    setTargetRecipients(e.target.value);
                    setSelectedUsers([]);
                    setSearchQuery("");
                  }}
                  style={{ cursor: "pointer" }}
                />
                <span style={{ textTransform: "capitalize" }}>
                  {option === "all"
                    ? "All Users"
                    : option === "professors"
                    ? "All Professors"
                    : option === "students"
                    ? "All Students"
                    : "Specific Users"}
                </span>
              </label>
            ))}
          </div>
        </div>

        {/* User Selection for Specific */}
        {targetRecipients === "specific" && (
          <div style={{ marginBottom: "20px" }}>
            <div style={{ marginBottom: "12px" }}>
              <input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: "100%",
                  padding: "10px 12px",
                  border: `1px solid ${colors.mainBlue}30`,
                  borderRadius: "6px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                  fontFamily: "inherit",
                  marginBottom: "12px",
                }}
              />
              <button
                type="button"
                onClick={handleSelectAllVisibleUsers}
                style={{
                  padding: "8px 12px",
                  backgroundColor: colors.mainBlue + "20",
                  color: colors.mainBlue,
                  border: `1px solid ${colors.mainBlue}30`,
                  borderRadius: "4px",
                  cursor: "pointer",
                  fontSize: "12px",
                  fontWeight: "600",
                }}
              >
                {filteredUsers.length > 0 &&
                filteredUsers.every((u) => selectedUsers.includes(u._id))
                  ? "Deselect All Visible"
                  : "Select All Visible"}
              </button>
            </div>

            {loading ? (
              <div style={{ color: colors.muted, fontSize: "14px" }}>
                Loading users...
              </div>
            ) : filteredUsers.length === 0 && searchQuery ? (
              <div style={{ color: colors.muted, fontSize: "14px" }}>
                No users found matching your search.
              </div>
            ) : (
              <div
                style={{
                  border: `1px solid ${colors.mainBlue}20`,
                  borderRadius: "6px",
                  maxHeight: "300px",
                  overflowY: "auto",
                }}
              >
                {filteredUsers.map((user) => (
                  <label
                    key={user._id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "10px",
                      padding: "12px",
                      borderBottom: `1px solid ${colors.mainBlue}10`,
                      cursor: "pointer",
                      backgroundColor: selectedUsers.includes(user._id)
                        ? colors.mainBlue + "05"
                        : "transparent",
                      transition: "background-color 0.2s",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedUsers.includes(user._id)}
                      onChange={() => handleUserToggle(user._id)}
                      style={{ cursor: "pointer" }}
                    />
                    <div style={{ flex: 1 }}>
                      <div
                        style={{
                          fontSize: "14px",
                          fontWeight: "500",
                          color: colors.mainBlue,
                        }}
                      >
                        {user.name}
                      </div>
                      <div
                        style={{
                          fontSize: "12px",
                          color: colors.muted,
                        }}
                      >
                        {user.email}
                      </div>
                    </div>
                    <span
                      style={{
                        padding: "2px 8px",
                        backgroundColor: colors.mainBlue + "20",
                        color: colors.mainBlue,
                        borderRadius: "4px",
                        fontSize: "11px",
                        fontWeight: "600",
                        textTransform: "capitalize",
                      }}
                    >
                      {user.role}
                    </span>
                  </label>
                ))}
              </div>
            )}
            {selectedUsers.length > 0 && (
              <div
                style={{
                  marginTop: "12px",
                  fontSize: "13px",
                  color: colors.muted,
                }}
              >
                {selectedUsers.length} user(s) selected
              </div>
            )}
          </div>
        )}

        {/* Submit Button */}
        <div style={{ display: "flex", gap: "12px" }}>
          <button
            type="submit"
            disabled={sending}
            style={{
              padding: "10px 20px",
              backgroundColor: colors.mainBlue,
              color: "white",
              border: "none",
              borderRadius: "6px",
              cursor: sending ? "not-allowed" : "pointer",
              fontSize: "14px",
              fontWeight: "600",
              opacity: sending ? 0.6 : 1,
            }}
          >
            {sending ? "Sending..." : "Send Notification"}
          </button>
          <button
            type="button"
            onClick={() => {
              setTitle("");
              setMessage("");
              setTargetRecipients("all");
              setSelectedUsers([]);
              setSearchQuery("");
              setErrorMessage("");
            }}
            style={{
              padding: "10px 20px",
              backgroundColor: "transparent",
              color: colors.mainBlue,
              border: `1px solid ${colors.mainBlue}30`,
              borderRadius: "6px",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "600",
            }}
          >
            Clear
          </button>
        </div>
      </form>

      {/* Info Box */}
      <div
        style={{
          marginTop: "24px",
          padding: "16px",
          backgroundColor: colors.isabelline,
          borderRadius: "6px",
          fontSize: "13px",
          color: colors.muted,
          lineHeight: "1.6",
        }}
      >
        <p style={{ margin: "0 0 8px 0", fontWeight: "600" }}>How to use:</p>
        <ul style={{ margin: "0", paddingLeft: "20px" }}>
          <li>
            Select your recipient type (All Users, All Professors, All Students,
            or Specific Users)
          </li>
          <li>For specific users, you can search by name or email</li>
          <li>Enter the notification title and message</li>
          <li>Click "Send Notification" to deliver the message</li>
        </ul>
      </div>
    </div>
  );
};

export default NotificationManagement;
